import { useState, useEffect, useCallback } from "react";
import { apiClient } from "../App";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { ChevronRight, ChevronDown, Users, Shield, Briefcase, Edit3 } from "lucide-react";
import { toast } from "sonner";

const BAND_PILL = {
  ready: "bg-emerald-100 text-emerald-700 border-emerald-200",
  nearly_ready: "bg-sky-100 text-sky-700 border-sky-200",
  action_required: "bg-amber-100 text-amber-700 border-amber-200",
  blocked: "bg-rose-100 text-rose-700 border-rose-200",
};

export function OrganisationStructureTree({ canMutate = true }) {
  const [tree, setTree] = useState({ nodes: [] });
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tr, lv] = await Promise.all([
        apiClient.get("/hierarchy/tree"),
        apiClient.get("/hierarchy/levels"),
      ]);
      setTree(tr.data || { nodes: [] });
      setLevels(lv.data || []);
    } catch (e) {
      toast.error("Failed to load organisation structure");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const seedDefaults = async () => {
    try {
      const r = await apiClient.post("/hierarchy/levels/seed-defaults");
      if (r.data.seeded) toast.success("Default hierarchy levels seeded");
      else toast.info(`${r.data.existingCount} level(s) already exist`);
      await load();
    } catch (e) {
      toast.error("Seed failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  const placeNow = async (userId, hierarchyLevelId) => {
    try {
      await apiClient.post("/hierarchy/assignments",
        { userId, hierarchyLevelId, managerUserId: null });
      toast.success("Placed in hierarchy");
      load();
    } catch (e) {
      toast.error("Place failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  const reassign = async (userId, managerUserId, hierarchyLevelId) => {
    try {
      await apiClient.put(`/hierarchy/assignments/${userId}`,
        { managerUserId: managerUserId || null, hierarchyLevelId });
      toast.success("Reassigned");
      setEditing(null);
      load();
    } catch (e) {
      toast.error("Reassign failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  const toggleExpand = (uid) => {
    setExpanded((p) => ({ ...p, [uid]: !p[uid] }));
  };

  const nodes = tree.nodes || [];
  const levelNameOf = (id) => (levels.find((l) => l.id === id) || {}).name || "—";

  const f = filter.toLowerCase();
  const matches = (n) => !f ||
    (n.name || "").toLowerCase().includes(f) ||
    (n.email || "").toLowerCase().includes(f);

  // Flatten tree into ordered rows by DFS using hierarchyPath ordering
  const rootNodes = nodes.filter((n) => !n.managerUserId);
  const rows = [];
  const pushDfs = (node, depth) => {
    if (matches(node)) rows.push({ node, depth });
    const kids = nodes.filter((x) => x.managerUserId === node.userId);
    const isOpen = expanded[node.userId];
    if (isOpen) kids.forEach((k) => pushDfs(k, depth + 1));
  };
  rootNodes.forEach((r) => pushDfs(r, 0));

  if (loading) {
    return <div className="text-sm text-slate-500" data-testid="hierarchy-tree-loading">
      Loading organisation structure…
    </div>;
  }

  if (!nodes.length) {
    return (
      <Card data-testid="hierarchy-tree-empty">
        <CardContent className="p-8 text-center">
          <Users className="h-10 w-10 mx-auto text-slate-300 mb-3" />
          <h3 className="text-base font-semibold text-slate-900">No organisation structure yet</h3>
          <p className="text-sm text-slate-500 mt-1 mb-4">
            Seed the default hierarchy (Owner → Manager → Team Leader → Fundraiser).
          </p>
          {canMutate && (
            <Button onClick={seedDefaults} data-testid="hierarchy-seed-defaults-btn">
              Seed default hierarchy
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <div data-testid="organisation-structure-tree" className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder="Filter by name or email"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="max-w-xs"
          data-testid="hierarchy-tree-filter"
        />
        <div className="text-xs text-slate-500">
          {nodes.length} member{nodes.length === 1 ? "" : "s"} ·{" "}
          {levels.length} hierarchy level{levels.length === 1 ? "" : "s"}
        </div>
        {canMutate && (
          <Button variant="outline" size="sm" onClick={load} data-testid="hierarchy-tree-refresh">
            Refresh
          </Button>
        )}
      </div>

      <div className="space-y-2">
        {rows.map(({ node, depth }) => {
          const childCount = nodes.filter((x) => x.managerUserId === node.userId).length;
          const isOpen = !!expanded[node.userId];
          const isEditing = editing === node.userId;
          return (
            <div key={node.userId} style={{ marginLeft: depth * 20 }}
                 data-testid={`hierarchy-node-${node.userId}`}>
              <Card className={node.unassigned ? "border-amber-200 bg-amber-50" : ""}>
                <CardContent className="py-3 px-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <button type="button"
                      onClick={() => childCount > 0 && toggleExpand(node.userId)}
                      className={`p-1 rounded ${childCount > 0 ? "hover:bg-slate-100" : "opacity-30"}`}
                      data-testid={`hierarchy-toggle-${node.userId}`}
                      disabled={childCount === 0}>
                      {childCount > 0 && isOpen
                        ? <ChevronDown className="h-4 w-4" />
                        : <ChevronRight className="h-4 w-4" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-900"
                              data-testid={`hierarchy-name-${node.userId}`}>
                          {node.name || node.email}
                        </span>
                        <Badge variant="outline" className="text-xs">
                          {levelNameOf(node.hierarchyLevelId)}
                        </Badge>
                        <Badge variant="outline" className="text-xs capitalize">
                          {node.role}
                        </Badge>
                        {node.unassigned && (
                          <Badge className="text-xs bg-amber-600">Unassigned</Badge>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">{node.email}</div>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <div className={`px-2 py-1 rounded border ${BAND_PILL[node.passportStatus] || "bg-slate-100 text-slate-600 border-slate-200"}`}
                           data-testid={`hierarchy-passport-${node.userId}`}>
                        <Shield className="inline h-3 w-3 mr-1" />
                        {node.passportScore}/100
                      </div>
                      <div className="flex items-center gap-1 text-slate-600">
                        <Briefcase className="h-3 w-3" />
                        <span data-testid={`hierarchy-campaigns-${node.userId}`}>
                          {node.campaignsAssigned}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-slate-600">
                        <Users className="h-3 w-3" />
                        <span data-testid={`hierarchy-reports-${node.userId}`}>
                          {node.directReports}
                        </span>
                      </div>
                      {canMutate && !node.unassigned && (
                        <Button variant="ghost" size="sm"
                          onClick={() => setEditing(isEditing ? null : node.userId)}
                          data-testid={`hierarchy-edit-${node.userId}`}>
                          <Edit3 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {canMutate && node.unassigned && levels.length > 0 && (
                        <Select onValueChange={(v) => placeNow(node.userId, v)}>
                          <SelectTrigger className="h-8 text-xs"
                            data-testid={`hierarchy-place-${node.userId}`}>
                            <SelectValue placeholder="Place…" />
                          </SelectTrigger>
                          <SelectContent>
                            {levels.map((l) => (
                              <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  </div>
                  {isEditing && (
                    <ReassignRow
                      node={node} levels={levels} allNodes={nodes}
                      onCancel={() => setEditing(null)}
                      onSave={(mgr, lvl) => reassign(node.userId, mgr, lvl)}
                    />
                  )}
                </CardContent>
              </Card>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ReassignRow({ node, levels, allNodes, onCancel, onSave }) {
  const [newMgr, setNewMgr] = useState(node.managerUserId || "__none__");
  const [newLevel, setNewLevel] = useState(node.hierarchyLevelId || "");
  return (
    <div className="mt-3 pt-3 border-t border-slate-200 flex flex-wrap items-center gap-2"
         data-testid={`hierarchy-reassign-${node.userId}`}>
      <Select value={newLevel} onValueChange={setNewLevel}>
        <SelectTrigger className="h-8 text-xs w-44"
          data-testid={`hierarchy-reassign-level-${node.userId}`}>
          <SelectValue placeholder="Level" />
        </SelectTrigger>
        <SelectContent>
          {levels.map((l) => (
            <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={newMgr} onValueChange={setNewMgr}>
        <SelectTrigger className="h-8 text-xs w-56"
          data-testid={`hierarchy-reassign-manager-${node.userId}`}>
          <SelectValue placeholder="Manager" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">— No manager (root) —</SelectItem>
          {allNodes.filter((n) => n.userId !== node.userId).map((c) => (
            <SelectItem key={c.userId} value={c.userId}>
              {c.name || c.email}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button size="sm"
        onClick={() => onSave(newMgr === "__none__" ? null : newMgr, newLevel || node.hierarchyLevelId)}
        data-testid={`hierarchy-reassign-save-${node.userId}`}>
        Save
      </Button>
      <Button size="sm" variant="ghost" onClick={onCancel}
              data-testid={`hierarchy-reassign-cancel-${node.userId}`}>
        Cancel
      </Button>
    </div>
  );
}
